/**
 * Stroke Reveal Particles (SVG path-driven)
 *
 * Goal:
 *   Make the logo appear by having "comet" particles travel *along the SVG strokes*
 *   and leave ribbon trails behind them. This is crisp because we follow the real
 *   vector paths (via getPointAtLength), not a raster distance field.
 *
 * How it works:
 *   1) Fetch the SVG file as text.
 *   2) Parse it and attach a hidden <svg> to the DOM (required for getTotalLength()).
 *   3) Sample each <path> ONCE into SVG-space polylines and cache them (_rawSamples).
 *      This is the only place getPointAtLength() is called — it runs once, at load.
 *   4) On every resize (or first paint), sampleToCanvas() just applies the current
 *      scale+offset transform to the cached raw points. No getPointAtLength, no
 *      getBBox, no reflow. O(n) multiply.
 *   5) Particles travel along the transformed polylines and draw bucketed ribbon trails.
 *
 * Performance notes:
 *   - getPointAtLength: called once total, at SVG load. Never again.
 *   - getBBox: called once total, at SVG load. Never again.
 *   - resize: just a transform loop over cached points + particle respawn.
 *   - Trail draw: TRAIL_BUCKETS stroke() calls per particle (~480/frame total).
 *   - lineCap: set once on the context, never touched again.
 */

import { getCanvasObserver } from "../utils/canvasObserver.js";

// ---------------------------------------------------------------------------
// Tuning
// ---------------------------------------------------------------------------

const DEV = {
  NO_BASE_GLOW: false,
  NO_ADDITIVE: false,
  NO_SPARKS: false,
  NO_BRIDGES: false,
  BUCKETS: 4, // e.g. 4 to test
};

const DEFAULT_FPS = 60;
const UNTHROTTLED_FPS = 55; // if desiredFPS >= this, use pure RAF

const PARTICLE_COUNT = 60;
const LOGO_SCALE = 0.94;

// Sampling resolution: only affects the ONE-TIME load sample.
// 3.5px gives smooth curves at logo scale (~200-300px). Go lower only if curves look faceted.
const MIN_PATH_LENGTH = 50;
const SAMPLE_PX = 45;

// Pause / pooling (makes ink “linger” and thickens the outline)
const PAUSE_CHANCE = 0.045; // chance per update to begin a pause (0.02–0.08)
const PAUSE_FRAMES_MIN = 6; // how long a pause lasts (frames)
const PAUSE_FRAMES_MAX = 18;

// Particle motion along paths
const SPEED_MIN = 0.5;
const SPEED_MAX = 3;
const DIR_FLIP_CHANCE = 0.35;

// Trail
const TRAIL_MAX_LENGTH_MIN = 50;
const TRAIL_MAX_LENGTH_MAX = 120;
const TRAIL_HEAD_WIDTH = 2.6;
const TRAIL_TAIL_WIDTH = 0.22;
const TRAIL_HEAD_ALPHA = 0.78;
const TRAIL_TAIL_ALPHA = 0.2;
const TRAIL_BUCKETS = 8;

// Glow / neon passes
const GLOW_ENABLED = true;
const ADDITIVE_BLEND = true; // true => "lighter", false => "source-over"
const GLOW_ALPHA_MULT = 0.35; // base glow alpha factor
const GLOW_WIDTH_MULT = 2.2; // base glow width factor
const GLOW_BLUR = 6; // blur radius

// Ignition boost for the base glow tube
const IGNITION_BASE_GLOW_BOOST = 1.35; // alpha multiplier during ignition (1.15–1.8)
const IGNITION_BLUR_BOOST = 1.25; // blur multiplier during ignition (1.0–1.6)

// Power → rendering curves (feel free to tweak)
const CORE_BASELINE_ALPHA = 0.03; // ember when off (0 = fully off)
const GLOW_BASELINE_ALPHA = 0.0; // glow should be basically 0 when off

// Flicker glow pass
const FLICKER_GLOW_ENABLED = true;
const FLICKER_JITTER_MAX = 0.6; // px, scaled by (1 - eased)
const FLICKER_MIN = 0.35; // clamp range
const FLICKER_MAX = 2.2;
const FLICKER_BASELINE = 0.75; // your 0.75
const FLICKER_SLOW_AMOUNT = 0.55; // your 0.55
const FLICKER_GLOW_ALPHA_MULT = 0.35; // multiplies alpha * flicker
const FLICKER_WIDTH_BASE = 2.0; // your 2.0
const FLICKER_WIDTH_AMOUNT = 0.35; // your 0.35

// Flicker / energy modulation
const FLICKER_SLOW_SPEED = 0.004; // slow breathing
const FLICKER_FAST_SPEED = 0.03; // fast buzz
const FLICKER_FAST_AMOUNT = 0.22; // buzz strength
const FLICKER_SPARK_CHANCE = 0.06; // spike probability
const FLICKER_SPARK_BOOST = 0.9; // spike intensity

// Optional extra “neon pop” hot core
const HOT_CORE_ENABLED = true;
const HOT_CORE_ALPHA_MULT = 0.35; // how bright the hot core is
const HOT_CORE_WIDTH_MULT = 0.9; // thinner than core

const SPARK_BUDGET_PER_FRAME = 3;
const BRIDGE_BUDGET_PER_FRAME = 2;

// Sparks / arcs
const SPARK_CHANCE = 0.01; // per particle per frame
const SPARK_LENGTH_MIN = 6;
const SPARK_LENGTH_MAX = 18;
const SPARK_ALPHA = 0.65;
const SPARK_WIDTH = 0.9;

// Sparks new
// Sparks / arcs (improved)
const SPARK_ENABLED = true;
const SPARKS_PER_HIT_MIN = 1;
const SPARKS_PER_HIT_MAX = 3;
const SPARK_BEND_MIN = 2; // arc bend amount
const SPARK_BEND_MAX = 10;
const SPARK_GLOW_ALPHA_MULT = 0.35; // glow pass alpha
const SPARK_GLOW_WIDTH_MULT = 2.2; // glow pass width
const SPARK_JITTER = 0.6; // tiny randomness

// Spark forks (snap-back)
const SPARK_FORK_ENABLED = true;
const SPARK_FORK_CHANCE = 0.55; // chance a spark gets a fork (0.2–0.8)
const SPARK_FORK_LENGTH_MULT = 0.55; // fork length relative to main (0.3–0.8)
const SPARK_FORK_BEND_MULT = 0.7; // fork bend relative to main (0.4–1.0)
const SPARK_FORK_ALPHA_MULT = 0.75; // fork alpha multiplier (0.4–1.0)
const SPARK_FORK_WIDTH_MULT = 0.85; // fork width multiplier (0.6–1.1)

// Electric bridges (cross-path arcs)
const BRIDGE_ENABLED = true;
const BRIDGE_CHANCE = 0.025; // per bucket/frame (0.01–0.06)
const BRIDGE_MIN_DIST = 10; // px
const BRIDGE_MAX_DIST = 60; // px
const BRIDGE_ALPHA = 0.55;
const BRIDGE_WIDTH = 0.9;
const BRIDGE_GLOW_ALPHA_MULT = 0.35;
const BRIDGE_GLOW_WIDTH_MULT = 2.2;
const BRIDGE_BEND_MIN = 4;
const BRIDGE_BEND_MAX = 18;
const BRIDGE_JITTER = 0.8;

// Power ramp (neon on/off)
const POWER_RAMP_IN_MS = 650; // how fast it turns on
const POWER_RAMP_OUT_MS = 280; // how fast it turns off
const POWER_FLICKER_TIME_MS = 900; // extra flicker window after turning on
const POWER_MIN_ON = 0.12; // initial visible glow at start of turn-on

const POWER_MAX = 2.8; // was 1.6, overdrive/ignition

// Micro wobble
const WOBBLE_AMPLITUDE = 0.9; // px
const WOBBLE_SPEED = 0.006; // time-based

// Steering smoothing
const ANGLE_SMOOTH = 0.22;

const MIN_SMOOTH_POINTS = 4;

// Mouse interaction
const MOUSE_ENABLED = false;
const MOUSE_SPAWN_BURST = 2;
const MOUSE_SPAWN_COOLDOWN_MS = 30;

// Colors
const COLORS = ["#e55c00", "#f77f00", "#ff9d00", "#ffb84d", "#ffcc80", "#a53f00ff"];

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------
function lerpAngle(from, to, t) {
  let diff = to - from;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  return from + diff * t;
}

function clamp(v, a, b) {
  return v < a ? a : v > b ? b : v;
}

function parseHex(hex) {
  return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}

// ---------------------------------------------------------------------------
// Hidden SVG host
// ---------------------------------------------------------------------------
function ensureHiddenSvgHost() {
  let host = document.getElementById("stroke-reveal-svg-host");
  if (host) return host;

  host = document.createElement("div");
  host.id = "stroke-reveal-svg-host";
  host.style.cssText =
    "position:absolute;left:-99999px;top:0;width:0;height:0;overflow:hidden;opacity:0;pointer-events:none;";
  document.body.appendChild(host);
  return host;
}

// ---------------------------------------------------------------------------
// PathSampler
// ---------------------------------------------------------------------------
class PathSampler {
  constructor(svgUrl) {
    this.svgUrl = svgUrl;
    this.host = ensureHiddenSvgHost();
    this.svgEl = null;
    this.paths = [];
    this.viewBox = { x: 0, y: 0, w: 1, h: 1 };

    // One-time caches, populated during load(), never recomputed
    this._cachedBBox = null; // union bbox of all paths in SVG user units
    this._rawSamples = []; // Array<{ points: [{x,y,angle}], closed: boolean }>
    // all coords in SVG user-unit space
  }

  /**
   * Fetch SVG, inject into DOM, run the expensive stuff ONCE:
   *   getBBox() — for centering
   *   getPointAtLength() loop — for sampling curves into polylines
   * After this, _rawSamples + _cachedBBox are populated and load() is never called again.
   */
  async load() {
    const res = await fetch(this.svgUrl, { cache: "force-cache" });
    if (!res.ok) throw new Error(`Failed to fetch SVG: ${res.status}`);
    const svgText = await res.text();

    const parser = new DOMParser();
    const doc = parser.parseFromString(svgText, "image/svg+xml");
    const parsedSvg = doc.querySelector("svg");
    if (!parsedSvg) throw new Error("SVG parse error: no <svg> root");

    if (this.svgEl && this.svgEl.parentNode) this.svgEl.parentNode.removeChild(this.svgEl);

    const svgNS = "http://www.w3.org/2000/svg";
    const svgEl = document.createElementNS(svgNS, "svg");

    const vb = parsedSvg.getAttribute("viewBox");
    if (vb) svgEl.setAttribute("viewBox", vb);
    const width = parsedSvg.getAttribute("width");
    const height = parsedSvg.getAttribute("height");
    if (width) svgEl.setAttribute("width", width);
    if (height) svgEl.setAttribute("height", height);

    svgEl.innerHTML = parsedSvg.innerHTML;
    this.host.appendChild(svgEl);
    this.svgEl = svgEl;

    this.viewBox = this._readViewBox(svgEl);

    this.paths = Array.from(svgEl.querySelectorAll("path")).filter((p) => {
      const d = p.getAttribute("d");
      return d && d.trim().length > 0;
    });

    if (!this.paths.length) throw new Error("No <path> elements found in SVG.");

    // --- ONE-TIME expensive work ---
    this._cachedBBox = this._computeBBox(); // getBBox() calls happen here
    this._rawSamples = await this._sampleAllPaths();
  }

  _readViewBox(svgEl) {
    const vb = svgEl.getAttribute("viewBox");
    if (vb) {
      const parts = vb
        .trim()
        .split(/[\s,]+/)
        .map(Number);
      if (parts.length === 4 && parts.every((n) => Number.isFinite(n))) {
        return { x: parts[0], y: parts[1], w: parts[2], h: parts[3] };
      }
    }
    const w = Number(svgEl.getAttribute("width")) || 1;
    const h = Number(svgEl.getAttribute("height")) || 1;
    return { x: 0, y: 0, w, h };
  }

  /** getBBox() — called once, result cached forever */
  _computeBBox() {
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    for (const p of this.paths) {
      let b;
      try {
        b = p.getBBox();
      } catch {
        continue;
      }
      if (!b) continue;
      minX = Math.min(minX, b.x);
      minY = Math.min(minY, b.y);
      maxX = Math.max(maxX, b.x + b.width);
      maxY = Math.max(maxY, b.y + b.height);
    }

    if (!Number.isFinite(minX)) {
      const vb = this.viewBox;
      return { x: vb.x, y: vb.y, w: vb.w, h: vb.h };
    }
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }

  /**
   * getPointAtLength() loop — called once, results cached in _rawSamples.
   * All points are in SVG user-unit space. Tangent angles are computed here too
   * (they're invariant to scale/translate so they never need recomputing).
   */
  async _sampleAllPaths() {
    const sampled = [];
    let pointsSinceYield = 0;

    for (const path of this.paths) {
      let totalLen = 0;
      try {
        totalLen = path.getTotalLength();
      } catch {
        continue;
      }
      if (!Number.isFinite(totalLen) || totalLen <= 0) continue;

      // (skip tiny paths)
      if (totalLen < MIN_PATH_LENGTH) continue;

      const steps = Math.max(12, Math.floor(totalLen / SAMPLE_PX));
      const pts = [];

      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const p = path.getPointAtLength(t * totalLen);
        pts.push({ x: p.x, y: p.y, angle: 0 });

        // Yield to keep the UI responsive
        pointsSinceYield++;
        if (pointsSinceYield >= 1200) {
          pointsSinceYield = 0;
          await new Promise((r) => requestAnimationFrame(r));
        }
      }

      for (let i = 0; i < pts.length; i++) {
        const p0 = pts[Math.max(0, i - 1)];
        const p1 = pts[Math.min(pts.length - 1, i + 1)];
        pts[i].angle = Math.atan2(p1.y - p0.y, p1.x - p0.x);
      }

      const a = pts[0],
        b = pts[pts.length - 1];
      const dx = b.x - a.x,
        dy = b.y - a.y;
      sampled.push({ points: pts, closed: dx * dx + dy * dy < 9 });
    }

    return sampled;
  }

  /**
   * Transform cached raw samples into canvas coordinates.
   * This is the ONLY method called on resize. It's a simple O(n) loop of
   * multiplies — no getPointAtLength, no getBBox, no reflow.
   */
  sampleToCanvas(canvasW, canvasH, logoScale = LOGO_SCALE) {
    if (!this._rawSamples.length || !this._cachedBBox) return [];

    const bb = this._cachedBBox;
    const scale = Math.min(canvasW / bb.w, canvasH / bb.h) * logoScale;
    const offsetX = (canvasW - bb.w * scale) * 0.5 - bb.x * scale;
    const offsetY = (canvasH - bb.h * scale) * 0.5 - bb.y * scale;

    // Map each cached path: just apply scale + offset to x,y. Angles are reused as-is.
    const result = new Array(this._rawSamples.length);

    for (let i = 0; i < this._rawSamples.length; i++) {
      const raw = this._rawSamples[i];
      const rawPts = raw.points;
      const pts = new Array(rawPts.length);

      for (let j = 0; j < rawPts.length; j++) {
        pts[j] = {
          x: rawPts[j].x * scale + offsetX,
          y: rawPts[j].y * scale + offsetY,
          angle: rawPts[j].angle, // unchanged by uniform scale + translate
        };
      }

      result[i] = { points: pts, closed: raw.closed };
    }

    return result;
  }

  destroy() {
    if (this.svgEl && this.svgEl.parentNode) this.svgEl.parentNode.removeChild(this.svgEl);
    this.svgEl = null;
    this.paths = [];
    this._cachedBBox = null;
    this._rawSamples = [];
  }
}

class TrailRing {
  constructor(capacity) {
    this.capacity = Math.max(4, capacity | 0);
    this.xs = new Float32Array(this.capacity);
    this.ys = new Float32Array(this.capacity);
    this.start = 0; // index of oldest
    this.size = 0; // number of valid points
  }

  clear() {
    this.start = 0;
    this.size = 0;
  }

  push(x, y) {
    const cap = this.capacity;

    if (this.size < cap) {
      const idx = (this.start + this.size) % cap;
      this.xs[idx] = x;
      this.ys[idx] = y;
      this.size++;
      return;
    }

    // overwrite oldest
    this.xs[this.start] = x;
    this.ys[this.start] = y;
    this.start = (this.start + 1) % cap;
  }

  getX(i) {
    return this.xs[(this.start + i) % this.capacity];
  }
  getY(i) {
    return this.ys[(this.start + i) % this.capacity];
  }
}

// ---------------------------------------------------------------------------
// Particle
// ---------------------------------------------------------------------------
class Particle {
  constructor(effect, spawnNear) {
    this.effect = effect;
    this.pauseFramesLeft = 0;

    this.flickerPhase = Math.random() * Math.PI * 2;
    this.flickerSpeed = 0.06 + Math.random() * 0.12;

    this.pauseChance = 0.015 + Math.random() * 0.02;

    this.baseSpeed = SPEED_MIN + Math.random() * (SPEED_MAX - SPEED_MIN);
    this.dir = Math.random() < DIR_FLIP_CHANCE ? -1 : 1;

    this.color = COLORS[Math.floor(Math.random() * COLORS.length)];
    this.rgb = parseHex(this.color);

    this.maxLength = TRAIL_MAX_LENGTH_MIN + Math.floor(Math.random() * (TRAIL_MAX_LENGTH_MAX - TRAIL_MAX_LENGTH_MIN));

    // Ring buffer trail (NO push+shift allocations)
    this.trail = new TrailRing(this.maxLength);

    this.timer = this.maxLength * 2;

    this.angle = Math.random() * Math.PI * 2;

    this.pathIndex = 0;
    this.pointIndexFloat = 0;
    this.x = 0;
    this.y = 0;

    this.reset(spawnNear);
  }

  reset(spawnNear) {
    const { pathIndex, pointIndex } = this.effect.pickSpawnPoint(spawnNear);
    this.pathIndex = pathIndex;
    this.pointIndexFloat = pointIndex;

    const p = this.effect.getPoint(this.pathIndex, Math.floor(this.pointIndexFloat));
    this.x = p.x;
    this.y = p.y;
    this.angle = p.angle;

    this.trail.clear();
    this.trail.push(this.x, this.y);

    this.timer = this.maxLength * 2;

    if (Math.random() < 0.25) {
      this.color = COLORS[Math.floor(Math.random() * COLORS.length)];
      this.rgb = parseHex(this.color);
    }
  }

  update() {
    // micro-pause to slow motion & add organic feel
    if (Math.random() < this.pauseChance) {
      this.trail.push(this.x, this.y);
      return;
    }

    this.timer--;
    if (this.timer < 1) {
      // Old behavior: gradually shrink by shifting.
      // Ring buffer doesn’t shrink cheaply; simplest is reset.
      this.reset(false);
      return;
    }

    // Pause / pooling BEFORE movement
    if (this.pauseFramesLeft > 0) {
      this.pauseFramesLeft--;
      this.trail.push(this.x, this.y);
      return;
    }

    // Randomly start a pause
    if (Math.random() < PAUSE_CHANCE) {
      const span = PAUSE_FRAMES_MAX - PAUSE_FRAMES_MIN + 1;
      this.pauseFramesLeft = PAUSE_FRAMES_MIN + Math.floor(Math.random() * span);
      this.trail.push(this.x, this.y);
      return;
    }

    const path = this.effect.sampledPaths[this.pathIndex];
    if (!path || path.points.length < 2) {
      this.reset(false);
      return;
    }

    this.pointIndexFloat += this.baseSpeed * this.effect.speedScale * this.dir;

    const n = path.points.length;
    if (path.closed) {
      while (this.pointIndexFloat < 0) this.pointIndexFloat += n;
      while (this.pointIndexFloat >= n) this.pointIndexFloat -= n;
    } else {
      if (this.pointIndexFloat < 0) {
        this.pointIndexFloat = -this.pointIndexFloat;
        this.dir *= -1;
      }
      if (this.pointIndexFloat >= n) {
        this.pointIndexFloat = n - 1 - (this.pointIndexFloat - (n - 1));
        this.dir *= -1;
      }
    }

    const idx = Math.floor(this.pointIndexFloat);
    const p = path.points[clamp(idx, 0, n - 1)];

    this.angle = lerpAngle(this.angle, p.angle, ANGLE_SMOOTH);
    this.x = p.x;
    this.y = p.y;

    this.trail.push(this.x, this.y);
  }

  _drawSparkArc(ctx, x, y, segAngle, r, g, b, style) {
    const len = SPARK_LENGTH_MIN + Math.random() * (SPARK_LENGTH_MAX - SPARK_LENGTH_MIN);

    const dir = Math.random() < 0.5 ? -1 : 1;
    const spread = (Math.random() - 0.5) * 0.6;
    const a = segAngle + (Math.PI / 2) * dir + spread;

    const ex = x + Math.cos(a) * len;
    const ey = y + Math.sin(a) * len;

    const bend = SPARK_BEND_MIN + Math.random() * (SPARK_BEND_MAX - SPARK_BEND_MIN);
    const mx = (x + ex) * 0.5 + Math.cos(a + Math.PI / 2) * bend;
    const my = (y + ey) * 0.5 + Math.sin(a + Math.PI / 2) * bend;

    const j = SPARK_JITTER * (1 - style.eased);
    const jx = (Math.random() - 0.5) * j;
    const jy = (Math.random() - 0.5) * j;

    const drawArc = () => {
      ctx.beginPath();
      ctx.moveTo(x + jx, y + jy);
      ctx.quadraticCurveTo(mx + jx, my + jy, ex + jx, ey + jy);
      ctx.stroke();
    };

    ctx.save();
    ctx.globalCompositeOperation = style.blend;
    ctx.strokeStyle = `rgba(${r},${g},${b},${SPARK_ALPHA * SPARK_GLOW_ALPHA_MULT})`;
    ctx.lineWidth = SPARK_WIDTH * SPARK_GLOW_WIDTH_MULT;
    drawArc();
    ctx.restore();

    ctx.save();
    ctx.globalCompositeOperation = style.blend;
    ctx.strokeStyle = `rgba(${r},${g},${b},${SPARK_ALPHA})`;
    ctx.lineWidth = SPARK_WIDTH;
    drawArc();
    ctx.restore();

    if (SPARK_FORK_ENABLED && Math.random() < SPARK_FORK_CHANCE) {
      const forkLen = len * SPARK_FORK_LENGTH_MULT;
      const back = segAngle + (Math.random() - 0.5) * 0.8;

      const fx = ex + Math.cos(back) * forkLen;
      const fy = ey + Math.sin(back) * forkLen;

      const forkBend = bend * SPARK_FORK_BEND_MULT;
      const fmx = (ex + fx) * 0.5 + Math.cos(back + Math.PI / 2) * forkBend;
      const fmy = (ey + fy) * 0.5 + Math.sin(back + Math.PI / 2) * forkBend;

      ctx.save();
      ctx.globalCompositeOperation = style.blend;
      ctx.strokeStyle = `rgba(${r},${g},${b},${SPARK_ALPHA * SPARK_GLOW_ALPHA_MULT * SPARK_FORK_ALPHA_MULT})`;
      ctx.lineWidth = SPARK_WIDTH * SPARK_GLOW_WIDTH_MULT * SPARK_FORK_WIDTH_MULT;
      ctx.beginPath();
      ctx.moveTo(ex + jx, ey + jy);
      ctx.quadraticCurveTo(fmx + jx, fmy + jy, fx + jx, fy + jy);
      ctx.stroke();
      ctx.restore();

      ctx.save();
      ctx.globalCompositeOperation = style.blend;
      ctx.strokeStyle = `rgba(${r},${g},${b},${SPARK_ALPHA * SPARK_FORK_ALPHA_MULT})`;
      ctx.lineWidth = SPARK_WIDTH * SPARK_FORK_WIDTH_MULT;
      ctx.beginPath();
      ctx.moveTo(ex + jx, ey + jy);
      ctx.quadraticCurveTo(fmx + jx, fmy + jy, fx + jx, fy + jy);
      ctx.stroke();
      ctx.restore();
    }
  }

  _buildBucketPath(ctx, startIdx, endIdx, eased, now) {
    const x0 = this.trail.getX(startIdx);
    const y0 = this.trail.getY(startIdx);

    ctx.beginPath();
    ctx.moveTo(x0, y0);

    for (let i = startIdx + 1; i < endIdx - 1; i++) {
      const px = this.trail.getX(i);
      const py = this.trail.getY(i);
      const nxp = this.trail.getX(i + 1);
      const nyp = this.trail.getY(i + 1);

      const dx = nxp - px;
      const dy = nyp - py;
      const segLen2 = dx * dx + dy * dy;
      if (segLen2 < 1e-6) continue;

      const segAngle = Math.atan2(dy, dx);
      const wobble = Math.sin(now * WOBBLE_SPEED + i * 0.6) * WOBBLE_AMPLITUDE * (1 - eased);

      const ox = Math.cos(segAngle + Math.PI / 2) * wobble;
      const oy = Math.sin(segAngle + Math.PI / 2) * wobble;

      const mx = (px + nxp) * 0.5 + ox;
      const my = (py + nyp) * 0.5 + oy;

      ctx.quadraticCurveTo(px + ox, py + oy, mx, my);
    }

    const lx = this.trail.getX(endIdx - 1);
    const ly = this.trail.getY(endIdx - 1);
    ctx.lineTo(lx, ly);
  }

  _strokeBaseGlow(ctx, r, g, b, style, now) {
    if (!GLOW_ENABLED) return;
    if (DEV.NO_BASE_GLOW) return;

    ctx.save();
    ctx.globalCompositeOperation = style.blend;

    let ignition = 1;
    if (this.effect?.powerFlickerUntil && now < this.effect.powerFlickerUntil) {
      ignition = IGNITION_BASE_GLOW_BOOST;
    }

    ctx.shadowBlur = GLOW_BLUR;
    ctx.shadowColor = `rgba(${r},${g},${b},${style.glowAlpha})`;
    ctx.strokeStyle = `rgba(${r},${g},${b},${style.glowAlpha * GLOW_ALPHA_MULT * ignition})`;
    ctx.lineWidth = style.width * GLOW_WIDTH_MULT;
    ctx.stroke();
    ctx.restore();
  }

  _strokeFlickerGlow(ctx, r, g, b, style, now, bucket, startIdx) {
    if (!FLICKER_GLOW_ENABLED) return;

    const slow = 0.5 + 0.5 * Math.sin(now * FLICKER_SLOW_SPEED + bucket * 1.7);
    const fast = 0.5 + 0.5 * Math.sin(now * FLICKER_FAST_SPEED + startIdx * 0.12);

    let flicker = FLICKER_BASELINE + FLICKER_SLOW_AMOUNT * slow + FLICKER_FAST_AMOUNT * fast;
    if (Math.random() < FLICKER_SPARK_CHANCE) flicker += FLICKER_SPARK_BOOST;
    flicker = Math.max(FLICKER_MIN, Math.min(FLICKER_MAX, flicker));

    let ignition = 1;
    if (this.effect?.powerFlickerUntil && now < this.effect.powerFlickerUntil) ignition = 1.35;

    const jitter = FLICKER_JITTER_MAX * (1 - style.eased);

    // ctx.save();
    // ctx.translate((Math.random() - 0.5) * jitter, (Math.random() - 0.5) * jitter);
    ctx.globalCompositeOperation = style.blend;

    // You already set this to 0 for perf
    ctx.shadowBlur = 0;

    ctx.shadowColor = `rgba(${r},${g},${b},${style.glowAlpha})`;
    ctx.strokeStyle = `rgba(${r},${g},${b},${style.glowAlpha * FLICKER_GLOW_ALPHA_MULT * flicker * ignition})`;
    ctx.lineWidth = style.width * (FLICKER_WIDTH_BASE + FLICKER_WIDTH_AMOUNT * flicker * ignition);
    ctx.stroke();
    // ctx.restore();
  }

  _strokeCore(ctx, r, g, b, style) {
    ctx.save();
    ctx.globalCompositeOperation = "source-over";
    ctx.shadowBlur = 0;
    ctx.strokeStyle = `rgba(${r},${g},${b},${style.coreAlpha})`;
    ctx.lineWidth = style.width;
    ctx.stroke();
    ctx.restore();
  }

  _maybeSpark(ctx, r, g, b, style, startIdx, endIdx) {
    if (!SPARK_ENABLED) return;
    if (DEV.NO_SPARKS) return;
    if ((this.effect._sparkBudget ?? 0) <= 0) return;

    const chance = SPARK_CHANCE * (0.25 + 0.75 * (1 - style.eased));
    if (Math.random() >= chance) return;

    const count = SPARKS_PER_HIT_MIN + Math.floor(Math.random() * (SPARKS_PER_HIT_MAX - SPARKS_PER_HIT_MIN + 1));

    const idx = Math.floor((startIdx + endIdx) * 0.5);
    const iPrev = Math.max(startIdx, idx - 1);
    const iNext = Math.min(endIdx - 1, idx + 1);

    const px = this.trail.getX(idx),
      py = this.trail.getY(idx);
    const ppx = this.trail.getX(iPrev),
      ppy = this.trail.getY(iPrev);
    const pnx = this.trail.getX(iNext),
      pny = this.trail.getY(iNext);

    const dx = pnx - ppx;
    const dy = pny - ppy;
    const segLen2 = dx * dx + dy * dy;
    if (segLen2 < 1e-6) return;

    const segAngle = Math.atan2(dy, dx);

    const actual = Math.min(count, this.effect._sparkBudget | 0);
    for (let i = 0; i < actual; i++) {
      this._drawSparkArc(ctx, px, py, segAngle, r, g, b, style);
    }
    // this.effect._sparkBudget -= actual;
    // Or for more budget use
    this.effect._sparkBudget--;
  }

  _drawBridgeArc(ctx, ax, ay, bx, by, r, g, b, style) {
    const dx = bx - ax;
    const dy = by - ay;
    const d = Math.hypot(dx, dy);
    if (d < 1e-6) return;

    const ang = Math.atan2(dy, dx);
    const bend = BRIDGE_BEND_MIN + Math.random() * (BRIDGE_BEND_MAX - BRIDGE_BEND_MIN);
    const dir = Math.random() < 0.5 ? -1 : 1;

    const mx = (ax + bx) * 0.5 + Math.cos(ang + Math.PI / 2) * bend * dir;
    const my = (ay + by) * 0.5 + Math.sin(ang + Math.PI / 2) * bend * dir;

    const j = BRIDGE_JITTER * (1 - style.eased);
    const jx = (Math.random() - 0.5) * j;
    const jy = (Math.random() - 0.5) * j;

    ctx.save();
    ctx.globalCompositeOperation = style.blend;
    ctx.strokeStyle = `rgba(${r},${g},${b},${BRIDGE_ALPHA * BRIDGE_GLOW_ALPHA_MULT})`;
    ctx.lineWidth = BRIDGE_WIDTH * BRIDGE_GLOW_WIDTH_MULT;
    ctx.beginPath();
    ctx.moveTo(ax + jx, ay + jy);
    ctx.quadraticCurveTo(mx + jx, my + jy, bx + jx, by + jy);
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.globalCompositeOperation = style.blend;
    ctx.strokeStyle = `rgba(${r},${g},${b},${BRIDGE_ALPHA})`;
    ctx.lineWidth = BRIDGE_WIDTH;
    ctx.beginPath();
    ctx.moveTo(ax + jx, ay + jy);
    ctx.quadraticCurveTo(mx + jx, my + jy, bx + jx, by + jy);
    ctx.stroke();
    ctx.restore();
  }

  _maybeBridge(ctx, r, g, b, style, startIdx, endIdx) {
    if (!BRIDGE_ENABLED) return;
    if (DEV.NO_BRIDGES) return;
    if ((this.effect._bridgeBudget ?? 0) <= 0) return;

    const chance = BRIDGE_CHANCE * (0.25 + 0.75 * (1 - style.eased));
    if (Math.random() >= chance) return;

    const nodes = this.effect.bridgeNodes;
    if (!nodes || nodes.length < 2) return;

    const idx = Math.floor((startIdx + endIdx) * 0.5);
    const ax = this.trail.getX(idx);
    const ay = this.trail.getY(idx);

    let best = null;
    let bestD2 = Infinity;

    for (let t = 0; t < 18; t++) {
      const n = nodes[(Math.random() * nodes.length) | 0];
      const dx = n.x - ax;
      const dy = n.y - ay;
      const d2 = dx * dx + dy * dy;

      if (d2 < bestD2) {
        bestD2 = d2;
        best = n;
      }
    }

    if (!best) return;

    const d = Math.sqrt(bestD2);
    if (d < BRIDGE_MIN_DIST || d > BRIDGE_MAX_DIST) return;

    this._drawBridgeArc(ctx, ax, ay, best.x, best.y, r, g, b, style);

    this.effect._bridgeBudget--;
  }

  _strokeHotCore(ctx, r, g, b, style) {
    if (!HOT_CORE_ENABLED) return;

    ctx.save();
    ctx.globalCompositeOperation = style.blend;
    ctx.shadowBlur = 0;
    ctx.strokeStyle = `rgba(${r},${g},${b},${style.coreAlpha * HOT_CORE_ALPHA_MULT})`;
    ctx.lineWidth = style.width * HOT_CORE_WIDTH_MULT;
    ctx.stroke();
    ctx.restore();
  }

  draw(ctx, now, power) {
    const len = this.trail.size;
    if (len < MIN_SMOOTH_POINTS) return;

    const r = this.rgb[0],
      g = this.rgb[1],
      b = this.rgb[2];

    const buckets = DEV.BUCKETS ?? TRAIL_BUCKETS;
    const bucketSize = Math.max(2, Math.floor((len - 1) / buckets));

    for (let bucket = 0; bucket < buckets; bucket++) {
      const startIdx = bucket * bucketSize;
      const endIdx = bucket === buckets - 1 ? len : Math.min((bucket + 1) * bucketSize + 1, len);

      if (endIdx - startIdx < 3) continue;

      const midProgress = ((startIdx + endIdx) * 0.5) / (len - 1);
      const eased = midProgress * midProgress;

      const alpha = TRAIL_TAIL_ALPHA + eased * (TRAIL_HEAD_ALPHA - TRAIL_TAIL_ALPHA);
      const width = TRAIL_TAIL_WIDTH + eased * (TRAIL_HEAD_WIDTH - TRAIL_TAIL_WIDTH);

      const pwr = power ?? 1;
      const pVis = clamp(pwr, 0, 1);

      const coreAlpha = alpha * (CORE_BASELINE_ALPHA + (1 - CORE_BASELINE_ALPHA) * pVis);
      const glowAlpha = alpha * (GLOW_BASELINE_ALPHA + (1 - GLOW_BASELINE_ALPHA) * pVis);
      const widthP = width * (0.75 + 0.25 * pVis);

      const style = {
        coreAlpha,
        glowAlpha,
        width: widthP,
        eased,
        blend: DEV.NO_ADDITIVE ? "source-over" : ADDITIVE_BLEND ? "lighter" : "source-over",
      };

      this._buildBucketPath(ctx, startIdx, endIdx, eased, now);

      // last two for glow
      // const isHeadBucket = bucket >= buckets - 2;
      // glow only last bucket
      const isHeadBucket = bucket === buckets - 1;
      if (isHeadBucket) {
        this._strokeBaseGlow(ctx, r, g, b, style, now);
      }
      this._strokeFlickerGlow(ctx, r, g, b, style, now, bucket, startIdx);
      this._strokeCore(ctx, r, g, b, style);
      this._strokeHotCore(ctx, r, g, b, style);

      this._maybeSpark(ctx, r, g, b, style, startIdx, endIdx);
      this._maybeBridge(ctx, r, g, b, style, startIdx, endIdx);
    }
  }
}

// ---------------------------------------------------------------------------
// StrokeRevealEffect
// ---------------------------------------------------------------------------
class StrokeRevealEffect {
  constructor(canvas, ctx, sampler) {
    this.canvas = canvas;
    this.ctx = ctx;
    this.sampler = sampler;

    this.width = canvas.width;
    this.height = canvas.height;

    this.sampledPaths = [];
    this.particles = [];

    this.mouse = { x: 0, y: 0, active: false };
    this.lastMouseSpawn = 0;

    this.speedScale = 1.0;

    // Track last known canvas dimensions so resize() can skip if nothing changed
    this._lastW = -1;
    this._lastH = -1;

    this.bridgeNodes = [];
  }

  resize() {
    const w = this.canvas.width;
    const h = this.canvas.height;

    // Nothing changed — skip the whole thing (particles keep running on old data)
    if (w === this._lastW && h === this._lastH) return;
    this._lastW = w;
    this._lastH = h;

    this.width = w;
    this.height = h;

    // Cheap: just applies scale+offset to cached raw samples. No getPointAtLength.
    this.sampledPaths = this.sampler.sampleToCanvas(w, h, LOGO_SCALE);

    this.bridgeNodes = this._buildBridgeNodes(220); // 120–400 is fine

    const avgSpacing = this._estimateAvgSpacing();
    this.speedScale = avgSpacing > 0 ? clamp(2.2 / avgSpacing, 0.6, 2.4) : 1.0;

    // Respawn particles so they land on the new path positions
    this.particles = [];
    for (let i = 0; i < PARTICLE_COUNT; i++) this.particles.push(new Particle(this, false));
  }

  _buildBridgeNodes(maxNodes = 220) {
    const nodes = [];
    const paths = this.sampledPaths;

    if (!paths || !paths.length) return nodes;

    // Sample points from each path evenly (cheap + stable)
    for (let pi = 0; pi < paths.length; pi++) {
      const pts = paths[pi].points;
      if (!pts || pts.length < 8) continue;

      const stride = Math.max(6, Math.floor(pts.length / 18)); // ~18 nodes per path-ish
      for (let i = 0; i < pts.length; i += stride) {
        nodes.push({ x: pts[i].x, y: pts[i].y });
        if (nodes.length >= maxNodes) return nodes;
      }
    }

    return nodes;
  }

  _estimateAvgSpacing() {
    let count = 0,
      sum = 0;
    for (let i = 0; i < Math.min(6, this.sampledPaths.length); i++) {
      const pts = this.sampledPaths[i].points;
      for (let j = 1; j < Math.min(pts.length, 40); j++) {
        const dx = pts[j].x - pts[j - 1].x;
        const dy = pts[j].y - pts[j - 1].y;
        sum += Math.hypot(dx, dy);
        count++;
      }
    }
    return count ? sum / count : 2.0;
  }

  getPoint(pathIndex, pointIndex) {
    const path = this.sampledPaths[pathIndex];
    if (!path) return { x: 0, y: 0, angle: 0 };
    const pts = path.points;
    const idx = ((pointIndex % pts.length) + pts.length) % pts.length;
    return pts[idx];
  }

  pickSpawnPoint(spawnNearMouse) {
    const paths = this.sampledPaths;
    if (!paths.length) return { pathIndex: 0, pointIndex: 0 };

    if (spawnNearMouse && this.mouse.active) {
      let best = { pathIndex: 0, pointIndex: 0, d2: Infinity };
      for (let t = 0; t < 180; t++) {
        const pi = Math.floor(Math.random() * paths.length);
        const pts = paths[pi].points;
        if (!pts || pts.length < 2) continue;

        const idx = Math.floor(Math.random() * pts.length);
        const p = pts[idx];
        const dx = p.x - this.mouse.x;
        const dy = p.y - this.mouse.y;
        const d2 = dx * dx + dy * dy;

        if (d2 < best.d2) best = { pathIndex: pi, pointIndex: idx, d2 };
      }
      return { pathIndex: best.pathIndex, pointIndex: best.pointIndex };
    }

    const pathIndex = Math.floor(Math.random() * paths.length);
    const pts = paths[pathIndex].points;
    const pointIndex = Math.floor(Math.random() * (pts.length || 1));
    return { pathIndex, pointIndex };
  }

  spawnMouseBurst(now) {
    if (!this.mouse.active) return;
    if (now - this.lastMouseSpawn < MOUSE_SPAWN_COOLDOWN_MS) return;
    this.lastMouseSpawn = now;

    for (let i = 0; i < MOUSE_SPAWN_BURST; i++) {
      const idx = Math.floor(Math.random() * this.particles.length);
      this.particles[idx].reset(true);
    }
  }

  render(now) {
    if (MOUSE_ENABLED) this.spawnMouseBurst(now);

    // Reset budgets per frame (integers)
    this._sparkBudget = SPARK_BUDGET_PER_FRAME | 0;
    this._bridgeBudget = BRIDGE_BUDGET_PER_FRAME | 0;

    for (const p of this.particles) {
      p.update();
      p.draw(this.ctx, now, this.power);
    }

    // Safety: never carry negatives into next frame
    if (this._sparkBudget < 0) this._sparkBudget = 0;
    if (this._bridgeBudget < 0) this._bridgeBudget = 0;
  }
}

// ---------------------------------------------------------------------------
// StrokeRevealCanvas: lifecycle, observer, animation loop
// ---------------------------------------------------------------------------
class StrokeRevealCanvas {
  constructor(canvas, svgSrc) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.container = canvas.parentElement;

    // FPS
    this.desiredFPS = DEFAULT_FPS;

    this.unthrottledFPS = UNTHROTTLED_FPS; // if desiredFPS >= this => pure RAF
    this.forceRAF = true; // set true to disable timer throttling no matter what

    this.frameInterval = 1000 / this.desiredFPS;
    this._timerId = null; // setTimeout handle
    this._useTimer = this.desiredFPS < UNTHROTTLED_FPS;

    this.svgSrc = svgSrc;
    this.sampler = new PathSampler(svgSrc);

    this.effect = null;
    this.isReady = false;
    this.pendingStart = false;

    this.isAnimating = false;
    this.animationId = null;

    this._onResize = null;

    // Power on
    this.powerFrom = 0; // start value for current ramp
    this.powerTarget = 0; // 0..1
    this.powerStart = 0; // ms timestamp when ramp started
    this.power = 0; // 0..1 current
    this.isPowered = false; // controlled by your orchestrator (turnOn/turnOff)
    this.isVisible = true; // controlled by IntersectionObserver
    this.powerFlickerUntil = 0;

    this._igniteTimers = [];

    this.init();
  }

  async init() {
    try {
      await this.sampler.load(); // fetch + getBBox + getPointAtLength — ONE TIME
      this.initializeCanvas();
      this.setupEventListeners();
      this.isReady = true;

      if (this.pendingStart) {
        this.pendingStart = false;
        this.start();
      }
    } catch (e) {
      console.warn("StrokeReveal: init failed", e);
    }
  }

  setVisible(isVisible) {
    this.isVisible = isVisible;

    // If we’re visible and powered (or still fading out), keep animating
    if (this.shouldAnimate()) this._ensureAnimating();
    else this.stop();
  }

  shouldAnimate() {
    // animate if visible AND either powered OR still fading (power>0)
    return this.isVisible && (this.isPowered || this.power > 0.01);
  }

  _scheduleNextTick() {
    // If we're not animating, don't schedule anything
    if (!this.isAnimating) return;

    // High FPS: pure RAF is fine
    if (!this._useTimer) {
      this.animationId = requestAnimationFrame(this.animate);
      return;
    }

    // Low FPS: reduce callback frequency via setTimeout
    const delay = Math.max(0, this.frameInterval);
    this._timerId = setTimeout(() => {
      // Use RAF for actual paint alignment
      this.animationId = requestAnimationFrame(this.animate);
    }, delay);
  }

  _clearSchedules() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    if (this._timerId) {
      clearTimeout(this._timerId);
      this._timerId = null;
    }
  }

  _ensureAnimating() {
    if (!this.isAnimating) this.start();
  }

  turnOn() {
    this.isPowered = true;

    const now = performance.now();
    this.powerFlickerUntil = now + POWER_FLICKER_TIME_MS;

    // Start at a tiny visible baseline (optional)
    this._setPowerTarget(POWER_MIN_ON, now);

    this._runIgnitionRamp(now);

    if (this.shouldAnimate()) this._ensureAnimating();
  }

  turnOff() {
    this.isPowered = false;
    this.powerFlickerUntil = 0;

    for (const t of this._igniteTimers) clearTimeout(t);
    this._igniteTimers.length = 0;

    // ramp toward 0
    this._setPowerTarget(0, performance.now());
  }

  pulse() {
    if (!this.isPowered) return;

    const now = performance.now();

    // brief chaos window
    this.powerFlickerUntil = now + 1200;
    const script = [
      { t: 0, target: 0.0 },
      { t: 60, target: POWER_MAX }, // immediate full overdrive
      { t: 120, target: 0.1 },
      { t: 200, target: POWER_MAX },
      { t: 300, target: 0.3 },
      { t: 400, target: POWER_MAX },
      { t: 500, target: 0.5 },
      { t: 600, target: 1.0 }, // settle back to normal
    ];

    for (const step of script) {
      setTimeout(() => {
        if (!this.isPowered) return;
        this._setPowerTarget(step.target, performance.now());
      }, step.t);
    }
  }

  _setPowerTarget(target, now = performance.now()) {
    target = clamp(target, 0, POWER_MAX);

    // If nothing changes, do nothing
    if (target === this.powerTarget) return;

    this.powerFrom = this.power;
    this.powerTarget = target;
    this.powerStart = now;

    // if we’re going from 0 → something, ensure animation is running
    if (this.shouldAnimate()) this._ensureAnimating();
  }

  _runIgnitionRamp(now) {
    // cancel any existing
    for (const t of this._igniteTimers) clearTimeout(t);
    this._igniteTimers.length = 0;

    // A believable “neon sign trying to catch”
    // powerTarget flips rapidly, then stabilizes to ON.
    const script = [
      { t: 0, target: 0.15 },
      { t: 70, target: 0.0 },
      { t: 120, target: 0.35 },
      { t: 180, target: 0.05 },
      { t: 240, target: 0.7 },
      { t: 320, target: 0.2 },
      { t: 420, target: 1.0 }, // catches
    ];

    for (const step of script) {
      const id = setTimeout(() => {
        // Only apply if still powered (user could scroll away / turn off)
        if (!this.isPowered) return;
        this._setPowerTarget(step.target, performance.now());
        this._ensureAnimating();
      }, step.t);
      this._igniteTimers.push(id);
    }

    // ensure it ends truly ON
    const finalId = setTimeout(
      () => {
        if (!this.isPowered) return;
        this._setPowerTarget(1, performance.now());
      },
      script[script.length - 1].t + 120,
    );
    this._igniteTimers.push(finalId);
  }
  initializeCanvas() {
    const rect = this.canvas.getBoundingClientRect();
    const RENDER_SCALE = 1;
    const w = Math.max(1, (rect.width * RENDER_SCALE) | 0);
    const h = Math.max(1, (rect.height * RENDER_SCALE) | 0);

    if (this.canvas.width !== w) this.canvas.width = w;
    if (this.canvas.height !== h) this.canvas.height = h;

    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.globalCompositeOperation = "source-over";
    this.ctx.shadowBlur = 0;
    this.ctx.shadowColor = "rgba(0,0,0,0)";
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    this.ctx.lineCap = "round";
    this.ctx.lineJoin = "round";
    this.ctx.miterLimit = 2;

    if (!this.effect) this.effect = new StrokeRevealEffect(this.canvas, this.ctx, this.sampler);
    this.effect.canvas = this.canvas;
    this.effect.ctx = this.ctx;

    this.effect.resize();
  }

  setupEventListeners() {
    if (MOUSE_ENABLED) {
      this.canvas.addEventListener("mousemove", (event) => {
        const b = this.canvas.getBoundingClientRect();
        this.effect.mouse.x = (event.clientX - b.left) * (this.canvas.width / b.width);
        this.effect.mouse.y = (event.clientY - b.top) * (this.canvas.height / b.height);
        this.effect.mouse.active = true;
      });

      this.canvas.addEventListener("mouseleave", () => {
        this.effect.mouse.active = false;
      });

      this.canvas.addEventListener(
        "touchstart",
        (event) => {
          const touch = event.touches[0];
          const b = this.canvas.getBoundingClientRect();
          this.effect.mouse.x = (touch.clientX - b.left) * (this.canvas.width / b.width);
          this.effect.mouse.y = (touch.clientY - b.top) * (this.canvas.height / b.height);
          this.effect.mouse.active = true;

          if (
            touch.clientX >= b.left &&
            touch.clientX <= b.right &&
            touch.clientY >= b.top &&
            touch.clientY <= b.bottom
          ) {
            event.preventDefault();
          }
        },
        { passive: false, capture: true },
      );

      this.canvas.addEventListener(
        "touchmove",
        (event) => {
          const touch = event.touches[0];
          const b = this.canvas.getBoundingClientRect();
          this.effect.mouse.x = (touch.clientX - b.left) * (this.canvas.width / b.width);
          this.effect.mouse.y = (touch.clientY - b.top) * (this.canvas.height / b.height);
          this.effect.mouse.active = true;
        },
        { passive: false },
      );

      this.canvas.addEventListener("touchend", () => {
        this.effect.mouse.active = false;
      });
    }

    this.canvas.addEventListener("pointerdown", () => this.pulse());
    this.canvas.addEventListener("touchstart", () => this.pulse(), { passive: true });

    let resizeTimeout;
    this._onResize = () => {
      clearTimeout(resizeTimeout);

      const shouldResume = this.isAnimating || this.shouldAnimate();
      this.stop(); // <- important: stop scheduling frames before touching width/height

      resizeTimeout = setTimeout(() => {
        this.initializeCanvas();

        // Force a clean frame boundary after realloc
        if (shouldResume) {
          this.start();
        }
      }, 200);
    };
    window.addEventListener("resize", this._onResize);
  }

  animate = (t) => {
    if (!this.isAnimating) return;

    // Do one “frame” worth of work
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    const now = t;

    // --- power ramp update ---
    const goingUp = this.powerTarget > this.powerFrom;
    const duration = goingUp ? POWER_RAMP_IN_MS : POWER_RAMP_OUT_MS;

    const k = duration <= 0 ? 1 : Math.min(1, (now - this.powerStart) / duration);
    const eased = k * k * (3 - 2 * k); // smoothstep
    this.power = this.powerFrom + (this.powerTarget - this.powerFrom) * eased;

    // stop completely when off and basically invisible
    if (this.powerTarget === 0 && this.power < 0.01) {
      this.power = 0;
      this.stop();
      return;
    }

    // expose power + ignition window to effect
    this.effect.power = this.power;
    this.effect.powerFlickerUntil = this.powerFlickerUntil;

    // This is your heavy step (update + draw)
    this.effect.render(now);

    // Schedule next tick (timer-throttled if desiredFPS is low)
    this._scheduleNextTick();
  };

  start() {
    this.canvas.style.opacity = "1";
    if (this.isAnimating) return;
    if (!this.isReady || !this.effect) {
      this.pendingStart = true;
      return;
    }

    this.isAnimating = true;

    // Recompute timing mode in case desiredFPS changed
    this._useTimer = !this.forceRAF && this.desiredFPS < this.unthrottledFPS;
    this.frameInterval = 1000 / this.desiredFPS;

    this._clearSchedules();
    this._scheduleNextTick();
  }

  stop() {
    this.pendingStart = false;
    if (!this.isAnimating) return;
    this.isAnimating = false;
    this._clearSchedules();
  }

  destroy() {
    this.stop();
    if (this._onResize) window.removeEventListener("resize", this._onResize);
    this.sampler.destroy();
  }
}

// ---------------------------------------------------------------------------
// Public init
// ---------------------------------------------------------------------------
export function initFlowfieldParticles() {
  const canvas = document.getElementById("flowfield-canvas");
  if (!canvas) {
    console.warn("StrokeReveal canvas not found");
    return null;
  }

  const svgSrc = "img/oravio-head-simple-ears.svg";
  const strokeReveal = new StrokeRevealCanvas(canvas, svgSrc);

  // NEW: visibility control via CanvasObserver
  const observer = getCanvasObserver({ rootMargin: "150px", threshold: 0.01 });
  observer.observe(
    canvas,
    () => strokeReveal.setVisible(true),
    () => strokeReveal.setVisible(false),
  );

  return strokeReveal;
}
